﻿import { ParameterSchemaStation, ParameterSchema, ParameterSchemaSensor, ParameterInfo } from "../../API/Schema/ParameterSchema";
import { PlottingPage } from "./PlottingPage";

/** Flag used in generating Tree nodes, indicating whether SensorId's should be split (if they contain slashes) or not. */
const SPLIT_SENSOR_IDS: boolean = true;
/** Character used for splitting SensorId's when we want to generate multiple levels of sensor folders. */
const SPLIT_SENSOR_CHAR: string = '/';

export class DownloadTree {
    public tree: JQuery;
    public fancyTree: Fancytree.Fancytree;

    private preventStatusBarUpdates: boolean = false;

    private $statusBar: JQuery;
    private $btnAllClear: JQuery;
    private $btnAllSelect: JQuery;
    private $btnMatchPlotTree: JQuery;

    constructor(private parameterSchemaStation: ParameterSchemaStation, parameterSchema: ParameterSchema = null, $container: JQuery, private plottingPage: PlottingPage) {
        this.tree = $('<div class="midi_PlottingPage_download_panel_tree_container"></div>').appendTo($container);
        this.fancyTree = (<JQuery><any>$('<div id="tree" class="midi_PlottingPage_download_panel_tree"></div>')
            .appendTo(this.tree)
            .fancytree({
                checkbox: true,
                selectMode: 3,
                icons: false,
                click: (event, data) => {
                    if (!data.node.folder) {
                        data.node.toggleSelected();
                        return false;
                    }
                    return true;
                },
                select: (event, data) => {
                    if (!this.preventStatusBarUpdates) {
                        this.updateStatusBar();
                    }
                },
                source: this.getTreeNodes(parameterSchemaStation, parameterSchema),
                clickFolderMode: 3
            }))
            .fancytree('getTree');

        let $toolbarWrapper: JQuery = $('<div class="tree-toolbar-wrapper"></div>').appendTo($container);
        this.$statusBar = $('<div class="small text-muted"></div>').css({ fontStyle: 'italic' }).appendTo($toolbarWrapper);
        let $toolbar: JQuery = $('<div class="btn-toolbar btn-toolbar-sm"></div>').appendTo($toolbarWrapper);
        this.$btnMatchPlotTree = $('<button class="btn-link">Select same as plot (data with download permission)</button>').appendTo($toolbar);
        this.$btnAllSelect = $('<button class="btn-link hidden">Select all</button>').appendTo($toolbar);
        this.$btnAllClear = $('<button class="btn-link">Unselect all</button>').appendTo($toolbar);

        if (this.parameterSchemaStation) {
            //this.selectAllTreeNodes();
            this.fancyTree.getRootNode().children[0].setExpanded(true, { noAnimation: true });
            this.updateStatusBar(); //: NB: not needed here IF we use this.selectAllTreeNodes()...
        } else {
            //this.updateStatusBar();
            this.selectMatchPlotSelection();
        }

        this.$btnAllClear.on("click", (e) => {
            e.preventDefault();
            this.deselectAllTreeNodes();
        });

        this.$btnAllSelect.on("click", (e) => {
            e.preventDefault();
            this.selectAllTreeNodes();
        });

        this.$btnMatchPlotTree.on("click", (e) => {
            e.preventDefault();
            this.selectMatchPlotSelection();
        });
    }

    private getTreeNodes(parameterSchemaStation: ParameterSchemaStation, parameterSchema: ParameterSchema = null): StationNodeData[] {
        var children: StationNodeData[] = [];

        if (parameterSchemaStation !== null) {
            children.push(new StationNodeData(parameterSchemaStation));
        }

        if (parameterSchemaStation == null && parameterSchema !== null) {
            var stationIds: string[] = [];

            for (let stationId in parameterSchema.Stations) {
                if (parameterSchema.Stations[stationId].Station.Region == this.plottingPage.mainSite.hashData.Region) {
                    stationIds.push(stationId);
                }
            }

            stationIds.sort();

            stationIds.forEach((stationId: string) => {
                children.push(new StationNodeData(parameterSchema.Stations[stationId]));
            });
        }

        //hide checkbox for nodes that the user has no download permission for
        children.forEach(node => {
            let nrOfChildren = node.children.length;
            let hiddenChildren = 0;
            node.children.forEach(sensorNode => {
                if (!this.plottingPage.checkIfUserCanDownloadSensorData(node.station.Station.Id, sensorNode.sensorId)) {
                    sensorNode.hideCheckbox = true;
                    sensorNode.unselectable = true;
                    hiddenChildren += 1;
                    sensorNode.children.forEach(param => {
                        param.hideCheckbox = true;
                        param.unselectable = true;
                        param.children.forEach(variant => {
                            variant.hideCheckbox = true;
                            variant.unselectable = true;
                        });
                    });
                }
            });
            if (hiddenChildren == nrOfChildren) {
                node.hideCheckbox = true;
                node.unselectable = true;
            }
        })

        return children;
    }

    private updateStatusBar(): void {
        if (this.preventStatusBarUpdates) {
            return;
        }

        $(".midi_PlottingPage_warningBox").remove(); //: just in case some error/warning has been added to the popup (i.e. max number of params..)

        if (this.$statusBar) {
            this.$statusBar.text(`counting...`);

            let paramNodes = [];
            let allSelectedNodes: Fancytree.FancytreeNode[] = this.fancyTree.getSelectedNodes();
            allSelectedNodes.forEach((node: Fancytree.FancytreeNode) => {
                if (!node.isFolder()) {
                    paramNodes.push(node);
                }
            });
            let checkedCount: number = paramNodes.length; // NB: this is inaccurate, as it would count all parents too, when really we only want the last level (parameters) !!
            this.$statusBar.text(`${checkedCount} parameter(s) selected`);
        }
    }

    private deselectAllTreeNodes(): void {
        this.preventStatusBarUpdates = true;
        this.fancyTree.getSelectedNodes().forEach((n) => {
            n.setSelected(false);
            n.visitParents((p) => {
                p.setExpanded(false, { noAnimation: true });
            });
        });

        if (this.parameterSchemaStation !== null) {
            this.fancyTree.getRootNode().children[0].setExpanded(true, { noAnimation: true });
        }

        this.preventStatusBarUpdates = false;
        this.updateStatusBar();
    }

    private selectAllTreeNodes(): void {
        this.preventStatusBarUpdates = true;
        this.fancyTree.visit(node => { //: SEE: https://wwwendt.de/tech/fancytree/doc/jsdoc/Fancytree.html#expandAll (expandAll() method not available in 2.7 which is version in use at time of writing!)
            node.setSelected(true);
            if (node.hasChildren() !== false && node.isExpanded() !== true) {
                node.setExpanded(true, { noAnimation: true });
            }
        });

        this.preventStatusBarUpdates = false;
        this.updateStatusBar();
    }

    private selectMatchPlotSelection(): void {
        this.preventStatusBarUpdates = true;

        this.fancyTree.getSelectedNodes().forEach((n) => {
            n.setSelected(false);
            n.visitParents((p) => {
                p.setExpanded(false, { noAnimation: true });
            });
        });

        let plotTreeSelectedNodes: Fancytree.FancytreeNode[] = this.plottingPage.parameterTree.fancyTree.getSelectedNodes();

        plotTreeSelectedNodes.forEach((node) => {
            let key: string = '';
            try {
                key = node.key; //: NB: this used to be 'compiled' here from node.data, but not consistent, so have now setup .key on nodes in plot page ParameterTree, which should match the keys setup on nodes for this download dialog tree..
            } catch (err) {
                console.warn("[ERROR][selectMatchPlotSelection()..] While guessing key from plot tree selected nodes..", { error: err, plotTreeNode: node, key: key });
            }

            if (key !== '') {
                let match = this.fancyTree.getNodeByKey(key);
                // console.info("selectMatchPlotSelection(), KEY = ", key, { node: node, match: match, matchIsFolder: match && match.isFolder() });
                if (match && match.isFolder() == false) { //: NB: here we're checking if the match is a folder, as if it is we assume that the param variant in plot page is not listed in modal tree, so we should probably not be selecting it (as it would also select all its children which is unwanted)
                    match.setSelected(true);
                    match.visitParents((p) => {
                        p.setExpanded(true, { noAnimation: true });
                    });
                }
            }
        });

        this.preventStatusBarUpdates = false;
        this.updateStatusBar();
    }
}

class StationNodeData implements Fancytree.NodeData {
    public readonly NODE_TYPE: string = 'station';
    public title: string;
    public key: string;
    public hideCheckbox: boolean = false;
    public unselectable: boolean = true;
    public folder: boolean = true;
    public selected: boolean = false;
    public expanded: boolean = false;
    public _self: StationNodeData;

    public children: SensorNodeData[] = [];

    constructor(public station: ParameterSchemaStation) {
        this.title = station.Station.Label;
        this.key = `${station.Station.Id}`;

        var sensorIds: string[] = [];

        for (var sensorId in station.Sensors) {
            sensorIds.push(sensorId);
        }

        sensorIds.sort();

        sensorIds.forEach((sensorId: string) => {
            ///this.children.push(new SensorNodeData(this, sensorId, station.Sensors[sensorId]));
            let sensorNode = new SensorNodeData(this, sensorId, station.Sensors[sensorId]);
            if (sensorNode.needsPushingToStationChildren) {
                this.children.push(sensorNode);
            }
        });

        this._self = this;
    }

    /**
     * Provides incremental keys/paths, which are necessary in case of grouping re-arranged sensor nodes together.
     * @param _chunk The current chunk in question, or if no chunking, the sensorId.
     * @param chunks The chunks of the sensor ID
     * @param chunkIndex The index of the current chunk provided as _chunk
     * @returns The compiled key in format `StationId|IncrementalSplitSensorId`
     */
    public buildSensorPath = (_chunk: string, chunks: string[] = null, chunkIndex: number = null): string => {
        let _key = `${this.key}|`;

        if (chunks && chunks.slice(0, chunkIndex).length > 0) {
            _key += `${chunks.slice(0, chunkIndex).join(SPLIT_SENSOR_CHAR)}${SPLIT_SENSOR_CHAR}`;
        }

        _key += _chunk;
        return _key;
    }

    /**
     * Helper to read/search a flattened array of sensor nodes for this station (helpful mainly for regrouping if that option is enabled).
     * @param pathsOnly
     * @returns
     */
    public flattenSensorNodesRecursive(pathsOnly: boolean = false): any[] {
        let res: any[] = [];

        this.children.forEach(x => {
            res.push(pathsOnly ? x.path : x);
            x.flattenChildSensorNodesRecursive(pathsOnly, res);
        });

        return res;
    }

    /**
     * Helper to get the node to which a sensor & params need adding. Idea is to use this to add parameters to the final node given as a result. Warning: this may return
     * the station node rather than a parameter node (as it's the root node for this action), but this is very unlikely to be the case in reality, unless autoAdd is not true.
     * 
     * @param chunks The chunks (or leaves) of the sensor ID, this should be at the very last one string corresponding to a sensor ID if it is not meant to be a tree of sensors.
     * @param autoAdd Automatically add the relevant nodes? This should normally always be true.
     * @param srcSensorId The source sensorId that triggered this call. If null, it will be built from chunks.
     * @param srcSensor The source sensor parameters that triggered this call. If null, a blank dummy API.ParameterSchemaSensor will be generated.
     * @returns The node to add sensor parameters to normally.
     */
    public getSensorNodeRecursively(chunks: string[], autoAdd: boolean = true, srcSensorId: string = null, srcSensor: ParameterSchemaSensor = null): SensorNodeData { // StationNodeData | 
        let debug = false;

        if (debug) {
            console.info('StationNodeData.getSensorNodeRecursively()', { chunks: chunks, srcSensor: srcSensor }); // , srcSensorId: srcSensorId
        }

        let finalNode: SensorNodeData = null;
        let addTo: any = this; //: progressively change this to each parent leaf when looping through chunks.. staring with station node to start with.

        chunks.forEach((chunk, i) => {
            let flat = this.flattenSensorNodesRecursive(false);
            let path = this.buildSensorPath(chunk, chunks, i);
            let incrementalChunks = chunks.slice(0, i + 1);

            let match: any[] = flat.filter(x => x.path == path);
            if (debug) {
                console.info('StationNodeData.getSensorNodeRecursively() - CHUNKS LOOP', { chunk: chunk, i: i, path: path, match: match, incrementalChunks: incrementalChunks });
            }

            if (match.length == 1) {
                finalNode = match[0];
                addTo = match[0];
            } else if (match.length == 0) {
                if (autoAdd) {
                    let _srcSensor: ParameterSchemaSensor = {
                        Parameters: {},
                        SensorDefinition: {
                            DisplayName: chunk,
                            Combined: false,
                            Level: null,
                            SourceIndex: null,
                            Hidden: false,
                        },
                        UseLiveData: true,
                    };

                    if (i == chunks.length - 1) {
                        if (srcSensor !== null) {
                            _srcSensor = srcSensor;
                        }
                    }

                    let sensorNode = new SensorNodeData(this, srcSensorId, _srcSensor, this.buildSensorPath(incrementalChunks.join(SPLIT_SENSOR_CHAR)), chunk, false, false);
                    if (debug) {
                        console.info('StationNodeData.getSensorNodeRecursively() - CHUNKS LOOP - new SensorNodeData()', { sensorNode: sensorNode });
                    }
                    addTo.children.push(sensorNode);

                    addTo = sensorNode;
                    finalNode = sensorNode;
                } else {
                    console.warn(`StationNodeData.getSensorNodeRecursively() - ${match.length} matches found for path '${path}', and autoAdd param is false...`);
                }
            } else {
                console.warn(`StationNodeData.getSensorNodeRecursively() - ${match.length} matches found for path '${path}', when 1 or 0 expected. We're going to use the first result by default, but this may not be the wanted behaviour, in which case sensor IDs need to be checked.`);
                finalNode = match[0];
                addTo = match[0];
            }
        });

        if (debug) {
            console.info('StationNodeData.getSensorNodeRecursively() - FINAL NODE', { finalNode: finalNode, chunks: chunks });
        }

        return finalNode;
    }
}

class SensorNodeData implements Fancytree.NodeData {
    public readonly NODE_TYPE: string = 'sensor';
    public title: string;
    public key: string;
    public hideCheckbox: boolean = false;
    public unselectable: boolean = false;
    public folder: boolean = true;
    public selected: boolean = false;
    public expanded: boolean = false;
    public isSplitSensor: boolean = false;
    public needsPushingToStationChildren = true;

    public children: any[] = []; //public children: ParameterNodeData[] = [];

    constructor(public stationNode: StationNodeData, public sensorId: string, public sensor: ParameterSchemaSensor, public path: string = null, public label: string = null, allowHandlingParents: boolean = true, addParams: boolean = true) {
        this.title = (sensor.SensorDefinition && sensor.SensorDefinition.DisplayName) ? sensor.SensorDefinition.DisplayName : sensorId;
        if (this.label) {
            this.title = this.label;
        }
        this.key = `${stationNode.key}|${sensorId}`;

        if (path == null) {
            this.path = stationNode.buildSensorPath(sensorId); //: this should maybe be built on either split display name or 
        }


        let addParamsTo: SensorNodeData = this;

        let sensorPathFinal = sensorId;
        if (sensor.SensorDefinition && sensor.SensorDefinition.DisplayName) {
            sensorPathFinal = sensor.SensorDefinition.DisplayName; //: initially, we can set paths in sensor DisplayName - if this is not working then a new DB column may need to be added.
        }
        let splitSensorId = sensorPathFinal.split(SPLIT_SENSOR_CHAR); //: TODO HERE: change this to use the database param we want to split (DisplayName ? or other if we feel using the display name for this as a temporary solution works..)

        //: emulate/test nested sensors for Prelude:
        /*
        if (stationNode.key == 'Australia_Prelude' && sensorId.substring(0, 'Forecast '.length) == 'Forecast ') {
            splitSensorId = sensorId.split(' ');
        }
        if (stationNode.key == 'Australia_Prelude' && sensorId == 'Forecast_30min') {
            splitSensorId = sensorId.split('_');
        }
        if (stationNode.key == 'Australia_Prelude' && sensorId == 'Forecast') {
            console.info('Australia_Prelude.Forecast  EMULATED TO  Forecast/Main/Tests...');
            splitSensorId = ['Forecast', 'Main', 'Tests'];
            // sensorId = 'Forecast/Main/Tests';
        }
        */

        if (SPLIT_SENSOR_IDS && allowHandlingParents) {
            addParamsTo = stationNode.getSensorNodeRecursively(splitSensorId, true, sensorId, sensor) ?? this;
        }

        if (!addParams) {
            // console.warn('SensorNodeData DO NOT ADD PARAMS set.', sensorId, this.key, this.path, addParamsTo);
            return;
        }


        var parameterIds: string[] = [];

        for (var parameterId in sensor.Parameters) {
            parameterIds.push(parameterId);
        }

        parameterIds.sort();

        parameterIds.forEach((parameterId: string) => {
            addParamsTo.children.push(new ParameterNodeData(addParamsTo, parameterId, sensor.Parameters[parameterId]));
        });
    }

    public flattenChildSensorNodesRecursive(keysOnly: boolean = false, addTo: any[] = []): void {
        this.children.forEach(x => {
            if (x.NODE_TYPE == 'sensor') {
                addTo.push(keysOnly ? x.key : x);
                x.flattenChildSensorNodesRecursive(keysOnly, addTo);
            }
        });
    }
}

class ParameterNodeData implements Fancytree.NodeData {
    public readonly NODE_TYPE: string = 'parameter';
    public title: string;
    public key: string;
    public hideCheckbox: boolean = false;
    public unselectable: boolean = false;
    public folder: boolean = false;
    public selected: boolean = false;

    public children: VariantNodeData[] = [];

    constructor(public sensorNode: SensorNodeData, public parameterId: string, public parameterInfo: ParameterInfo) {
        this.title = (parameterInfo.Variants.length && parameterInfo.Variants[0].ParameterDefinition && parameterInfo.Variants[0].ParameterDefinition.DisplayName) ? parameterInfo.Variants[0].ParameterDefinition.DisplayName : parameterId;
        // this.folder = parameterInfo.Variants.length != 0; //: not entirely accurate, as we only add variants if the .Variant key is not null..

        this.key = `${sensorNode.key}|${parameterId}`;

        var variantNames: string[] = [];

        for (var v of parameterInfo.Variants) {
            if (v.Variant != null) {
                variantNames.push(v.Variant);
            } else if (v.Variant == null && parameterInfo.Variants.length > 1) {
                variantNames.push(v.Variant);
            }
        }

        variantNames.sort((a, b) => {
            let av = parseFloat(a);
            let bv = parseFloat(b);
            if (!isNaN(av) && !isNaN(bv)) {
                return av - bv;
            } else if (isNaN(av)) {
                return -1;
            } else if (isNaN(bv)) {
                return 1;
            } else {
                return a.localeCompare(b);
            }
        });

        variantNames.forEach((variantName: string) => {
            this.children.push(new VariantNodeData(sensorNode, variantName, parameterId, parameterInfo));
        });

        if (variantNames.length > 0) {
            this.folder = true;
        }
    }
}

class VariantNodeData implements Fancytree.NodeData {
    public readonly NODE_TYPE: string = 'variant';
    public title: string;
    public key: string;
    public data: any;
    public hideCheckbox: boolean = false;
    public unselectable: boolean = false;
    public folder: boolean = false;
    public selected: boolean = false;

    constructor(public sensorNode: SensorNodeData, public variantName: string, public parameterId: string, public parameterInfo: ParameterInfo) {
        this.title = variantName === null ? "Latest" : variantName;
        this.key = `${sensorNode.key}|${parameterId}|${variantName}`; //: NB: "null" added if we receive variantName===null, which is necessary for handling matches with plot page tree selections and also downloads instructions sent to server.
    }
}
 